import { capitalize as capitalizeFn } from 'lodash-es';

import { PackageType as APIPackageType } from '@viamrobotics/web-client/app/packages/v1/packages';
import * as APITypes from '@viamrobotics/web-client/app/v1/app';

export const NOT_YET_PUBLISHED = Symbol('not yet published');

export const RegistryItemTypes = {
  ML_MODEL: 'ml-model',
  ML_TRAINING: 'ml-training',
  MODULE: 'module',
  FRAGMENT: 'fragment',
  OTHER: 'other',
} as const;

export type RegistryItemType =
  (typeof RegistryItemTypes)[keyof typeof RegistryItemTypes];

export const TypeFilters = {
  ALL: 'All',
  ML_MODEL: 'ML Model',
  ML_TRAINING: 'Training Script',
  MODULE: 'Module',
  FRAGMENT: 'Fragment',
} as const;

export type TypeFilter = (typeof TypeFilters)[keyof typeof TypeFilters];

/** True when the selected type filter should include package registry items. */
export const typeFilterIncludesPackages = (type: TypeFilter): boolean =>
  type !== TypeFilters.FRAGMENT;

/** True when the selected type filter should include public fragments. */
export const typeFilterIncludesFragments = (type: TypeFilter): boolean =>
  type === TypeFilters.ALL || type === TypeFilters.FRAGMENT;

const TypeFilterMapping: Record<string, TypeFilter> = (() => {
  const mapping: Record<string, TypeFilter> = {};
  for (const value of Object.values(TypeFilters)) {
    mapping[value] = value;
  }
  return mapping;
})();

interface RegistryItemMetadata {
  [APIPackageType.ML_MODEL]: {
    oneofKind: 'mlModelMetadata';
    mlModelMetadata: APITypes.MLModelMetadata;
  };
  [APIPackageType.ML_TRAINING]: {
    oneofKind: 'mlTrainingMetadata';
    mlTrainingMetadata: APITypes.MLTrainingMetadata;
  };
  [APIPackageType.MODULE]: {
    oneofKind: 'moduleMetadata';
    moduleMetadata: APITypes.ModuleMetadata;
  };
}

export type RegistryItemOfType<ItemType extends keyof RegistryItemMetadata> =
  Omit<APITypes.RegistryItem, 'metadata' | 'type'> & {
    metadata: RegistryItemMetadata[ItemType];
    type: ItemType;
  };

export interface RegistryItemSummary extends APITypes.RegistryItem {
  author: string;
  registryURL: string;
  moduleResourceTypes: string[] | undefined;
  lastUpdated: Date | typeof NOT_YET_PUBLISHED;
  /**
   * Overrides {@link packageTypeProtoToRegistryItemType} for display when the
   * row is not a package (e.g. a public fragment listed on /registry).
   */
  registryItemType?: RegistryItemType;
}

/** Canonical registry item id for the official viam-server built-ins module. */
export const BUILTINS_REGISTRY_MODULE_ITEM_ID = 'viam:built-ins';

/** Minimal fields needed to detect the built-ins registry module in the UI. */
export type BuiltinsRegistryModuleLike = Pick<
  APITypes.RegistryItem,
  'itemId' | 'name' | 'publicNamespace'
>;

/**
 * True when the registry row is the official viam-server built-ins module (RDK models
 * exposed as registry items). Matches {@link BUILTINS_REGISTRY_MODULE_ITEM_ID}, with a
 * `publicNamespace` + `name` fallback when some responses omit or vary `itemId`.
 */
const normalizeBuiltinsModuleName = (name: string | undefined) =>
  (name ?? '').trim().toLowerCase().replaceAll('_', '-');

const normalizePublicNamespace = (ns: string | undefined) =>
  (ns ?? '').trim().toLowerCase();

export const isBuiltinsRegistryModule = (
  item: BuiltinsRegistryModuleLike | RegistryItemSummary
): boolean => {
  if (
    item.itemId === BUILTINS_REGISTRY_MODULE_ITEM_ID ||
    item.itemId.toLowerCase() === BUILTINS_REGISTRY_MODULE_ITEM_ID
  ) {
    return true;
  }
  return (
    normalizePublicNamespace(item.publicNamespace) === 'viam' &&
    normalizeBuiltinsModuleName(item.name) === 'built-ins'
  );
};

/** True when `moduleId` is the built-ins module (e.g. robot config `module_id`). */
export const isBuiltinsRegistryModuleId = (
  moduleId: string | undefined
): boolean => moduleId === BUILTINS_REGISTRY_MODULE_ITEM_ID;

export type ModelRegistryItemSummary =
  RegistryItemSummaryOfType<APIPackageType.ML_MODEL>;

export type RegistryItemSummaryOfType<
  ItemType extends keyof RegistryItemMetadata,
> = Omit<RegistryItemSummary, 'metadata' | 'type'> & {
  metadata: RegistryItemMetadata[ItemType];
  type: ItemType;
};

export interface ModelMetadata {
  description: string | undefined;
  supportedHardware: string[] | undefined;
}

export const filtersToListRegistryItemsRequest = (
  organizationID: string | undefined,
  publicNamespaces: string[] | undefined,
  type: TypeFilter,
  searchTerm?: string,
  showOwnedDeprecated = false
): APITypes.ListRegistryItemsRequest => {
  const visibilities = [APITypes.Visibility.PUBLIC];
  if (organizationID) {
    visibilities.push(
      APITypes.Visibility.PRIVATE,
      APITypes.Visibility.PUBLIC_UNLISTED
    );
  }

  const types: APIPackageType[] = [];
  switch (type) {
    case TypeFilters.ALL: {
      types.push(
        APIPackageType.MODULE,
        APIPackageType.ML_MODEL,
        APIPackageType.ML_TRAINING
      );
      break;
    }
    case TypeFilters.MODULE: {
      types.push(APIPackageType.MODULE);
      break;
    }
    case TypeFilters.ML_MODEL: {
      types.push(APIPackageType.ML_MODEL);
      break;
    }
    case TypeFilters.ML_TRAINING: {
      types.push(APIPackageType.ML_TRAINING);
      break;
    }
    case TypeFilters.FRAGMENT: {
      // Fragments are loaded separately from ListFragments, not ListRegistryItems.
      break;
    }
  }

  return {
    organizationId: organizationID,
    publicNamespaces: publicNamespaces ?? [],
    types,
    visibilities,
    platforms: [],
    statuses: [],
    ...(searchTerm && searchTerm.length > 0 && { searchTerm }),
    moduleSourceTypes: [],
    moduleLanguages: [],
    showOwnedDeprecated,
  };
};

export const parseStringAsTypeFilter = (
  stringType: string | undefined | null
): TypeFilter => {
  if (!stringType) {
    return TypeFilters.ALL;
  }
  const mapping = TypeFilterMapping[stringType];
  if (mapping) {
    return mapping;
  }

  return TypeFilters.ALL;
};

export const packageTypeProtoToRegistryItemType = (
  type: APIPackageType
): RegistryItemType => {
  switch (type) {
    case APIPackageType.ML_MODEL: {
      return RegistryItemTypes.ML_MODEL;
    }
    case APIPackageType.ML_TRAINING: {
      return RegistryItemTypes.ML_TRAINING;
    }
    case APIPackageType.MODULE: {
      return RegistryItemTypes.MODULE;
    }
    default: {
      return RegistryItemTypes.OTHER;
    }
  }
};

/**
 * toDisplay converts a registry item type to its display string
 * @param type the registry item type
 * @param capitalize true, to capitalize the display string
 * @returns the display string
 */
export const toDisplay = (
  type: RegistryItemType,
  capitalize?: boolean
): string => {
  let displayString = '';

  switch (type) {
    case RegistryItemTypes.ML_MODEL: {
      return 'ML model';
    }
    case RegistryItemTypes.ML_TRAINING: {
      displayString = 'training script';
      break;
    }
    case RegistryItemTypes.MODULE: {
      displayString = 'module';
      break;
    }
    case RegistryItemTypes.FRAGMENT: {
      displayString = 'fragment';
      break;
    }
    case RegistryItemTypes.OTHER: {
      displayString = 'other';
      break;
    }
  }

  return capitalize ? capitalizeFn(displayString) : displayString;
};

export const getRegistryItemModuleMetadata = (
  registryItem: RegistryItemSummary | APITypes.RegistryItem
): APITypes.ModuleMetadata | undefined => {
  if (
    registryItem.type !== APIPackageType.MODULE ||
    registryItem.metadata.oneofKind !== 'moduleMetadata'
  )
    return undefined;
  return registryItem.metadata.moduleMetadata;
};

export const getRegistryItemSupportedPlatforms = (
  registryItem: RegistryItemSummary
): string[] | undefined => {
  const versions = getRegistryItemModuleMetadata(registryItem)?.versions;
  if (!versions) return undefined;

  const platformsSet = new Set<string>();
  for (const version of versions) {
    for (const file of version.files) {
      platformsSet.add(file.platform);
    }
  }
  return [...platformsSet];
};

export const getMetadataForModel = (
  registryItem: RegistryItemSummary | undefined,
  modelName: string | symbol
): ModelMetadata => {
  const models = registryItem
    ? getRegistryItemModuleMetadata(registryItem)?.models
    : undefined;
  if (typeof modelName !== 'string' || !registryItem || !models) {
    return { description: undefined, supportedHardware: undefined };
  }

  const model = models.find((apiModel) => apiModel.model === modelName);
  const { description, supportedHardware } = model ?? {};
  return { description, supportedHardware };
};

export const TEST_ONLY = {
  EMPTY_MODULE_REGISTRY_ITEM_SUMMARY: {
    author: '',
    itemId: '',
    type: APIPackageType.MODULE,
    name: 'empty-module',
    url: '',
    totalRobotUsage: '0',
    totalOrganizationUsage: '0',
    totalExternalOrganizationUsage: '0',
    totalExternalRobotUsage: '0',
    registryURL: '',
    description: '',
    moduleResourceTypes: undefined,
    organizationId: '',
    publicNamespace: '',
    versions: [],
    metadata: {
      oneofKind: undefined,
    },
    visibility: APITypes.Visibility.PRIVATE,
    lastUpdated: NOT_YET_PUBLISHED,
  } as RegistryItemSummary,
  EMPTY_ML_MODEL_REGISTRY_ITEM_SUMMARY: {
    author: '',
    itemId: '',
    type: APIPackageType.ML_MODEL,
    name: 'empty-ml-model',
    url: '',
    totalRobotUsage: '0',
    totalOrganizationUsage: '0',
    totalExternalOrganizationUsage: '0',
    totalExternalRobotUsage: '0',
    registryURL: '',
    description: '',
    moduleResourceTypes: undefined,
    organizationId: '',
    publicNamespace: '',
    versions: [],
    metadata: {
      oneofKind: undefined,
    },
    visibility: APITypes.Visibility.PRIVATE,
    lastUpdated: NOT_YET_PUBLISHED,
  } as RegistryItemSummary,
};
